import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'throttle-debounce';

import { ELEMENTS_IDS } from 'shared/components/ApplicationLayout/constants/elements-ids';
import { useGetHeaderHeight } from 'shared/hooks/useGetHeaderHeight';
import {
  useListingLayoutFiltersHeightSelector,
  useListingLayoutIsShortSelector,
} from 'shared/store/clientSide/slices/listingLayout';

const THROTTLE_DELAY = 100;

// 20 - отступ карты от листинга, 20 - отступ самого листинга
const GAP_BETWEEN_MAP_AND_LISTING = 40;

export function useCalculateMapSizes(): void {
  const isMapOpen = useListingLayoutIsShortSelector();
  const filtersHeight = useListingLayoutFiltersHeightSelector();
  const headerHeight = useGetHeaderHeight();
  const fullHeaderHeight = filtersHeight + headerHeight;

  const mapContainer = useRef<HTMLElement | null>(null);
  const mapWrapper = useRef<HTMLElement | null>(null);
  const listing = useRef<HTMLElement | null>(null);

  useEffect(() => {
    mapContainer.current = document.getElementById(ELEMENTS_IDS.mapContainerNodeId);
    mapWrapper.current = document.getElementById(ELEMENTS_IDS.mapWrapperNodeId);
    listing.current = document.getElementById(ELEMENTS_IDS.listingNodeId);
  }, []);

  useEffect(() => {
    if (mapContainer.current) {
      mapContainer.current.style.top = `${fullHeaderHeight}px`;
    }

    if (mapWrapper.current) {
      mapWrapper.current.style.height = `calc(100vh - ${fullHeaderHeight}px)`;
    }
  }, [fullHeaderHeight]);

  const onWindowResize = useCallback(() => {
    if (listing.current) {
      const { right } = listing.current.getBoundingClientRect();
      // Учитываем скролл
      const contentWidth = document.documentElement.clientWidth;
      const updatedMapWidth = contentWidth - right - GAP_BETWEEN_MAP_AND_LISTING;

      if (mapWrapper.current) {
        mapWrapper.current.style.width = isMapOpen ? `${updatedMapWidth}px` : '20px';
        mapWrapper.current.style.height = `calc(100vh - ${fullHeaderHeight}px)`;
      }
    }
  }, [fullHeaderHeight, isMapOpen]);

  const onWindowResizeThrottled = useMemo(() => debounce(THROTTLE_DELAY, onWindowResize), [onWindowResize]);

  useEffect(() => {
    onWindowResize();
  }, [onWindowResize, isMapOpen]);

  useEffect(() => {
    window.addEventListener('resize', onWindowResizeThrottled);

    return (): void => {
      window.removeEventListener('resize', onWindowResizeThrottled);
    };
  }, [onWindowResizeThrottled]);
}
